import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase, IS_TEST } from "../config";
import { VERDICTS } from "../components/episode/SheetCheckPanel";
import { FaArrowLeft, FaTable, FaCheckCircle } from "react-icons/fa";

// Sheet check queue (2026-10-20): every episode where the old Google Sheets
// disagree with the database, most urgent first. Each row opens that episode
// in the Episode Editor at its Sheet check panel. Read-only here.

const ORDER = ["human", "use_sheet", "likely_both", "keep_db", "fill", "missing"];

export default function SheetCheckQueue() {
  const [items, setItems] = useState(null);
  const [episodes, setEpisodes] = useState({});
  const [err, setErr] = useState("");
  const [show, setShow] = useState("open"); // open | all
  const [kind, setKind] = useState("times"); // times | missing | all

  useEffect(() => {
    (async () => {
      const all = [];
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase.from("sheet_review_items")
          .select("item_id,episode_id,kind,verdict,status,resolved_by").range(from, from + 999);
        if (error) { setErr(error.message); return; }
        all.push(...data);
        if (data.length < 1000) break;
      }
      setItems(all);
      const ids = [...new Set(all.map((i) => i.episode_id))];
      const m = {};
      for (let i = 0; i < ids.length; i += 200) {
        const { data } = await supabase.from("episodes").select("episode_id,title,publish_date,episode_type").in("episode_id", ids.slice(i, i + 200));
        (data || []).forEach((e) => { m[e.episode_id] = e; });
      }
      setEpisodes(m);
    })();
  }, []);

  const rows = useMemo(() => {
    if (!items) return [];
    const by = {};
    items.filter((i) => kind === "all" || (kind === "missing" ? i.kind === "missing" : i.kind !== "missing")).forEach((i) => {
      const r = (by[i.episode_id] = by[i.episode_id] || { episode_id: i.episode_id, open: 0, done: 0, counts: {}, people: new Set() });
      if (i.status === "open") { r.open += 1; r.counts[i.verdict] = (r.counts[i.verdict] || 0) + 1; } else { r.done += 1; if (i.resolved_by) r.people.add(i.resolved_by); }
    });
    const rank = (r) => { const k = ORDER.findIndex((v) => r.counts[v]); return k < 0 ? 99 : k; };
    return Object.values(by).filter((r) => show === "all" || r.open > 0)
      .sort((a, b) => rank(a) - rank(b) || (episodes[b.episode_id]?.publish_date || "").localeCompare(episodes[a.episode_id]?.publish_date || ""));
  }, [items, episodes, show, kind]);

  const totals = useMemo(() => {
    if (!items) return null;
    const t = items.filter((i) => i.kind !== "missing");
    return { times: t.length, timesOpen: t.filter((i) => i.status === "open").length,
      missing: items.length - t.length, missingOpen: items.filter((i) => i.kind === "missing" && i.status === "open").length };
  }, [items]);

  const pill = "text-[10px] px-2 py-0.5 rounded-full font-semibold";
  const tab = (on) => `px-3 py-1 rounded-lg text-xs font-semibold ${on ? "bg-kk-gold text-kk-ink" : "bg-white/5 text-white/60 hover:text-white"}`;

  return (
    <div className="min-h-screen bg-kk-ink text-white">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        <Link to="/episode-creator" className="flex items-center gap-2 text-kk-gold/70 hover:text-kk-gold text-sm"><FaArrowLeft className="text-xs" /> Episodes</Link>
        <header className="space-y-1">
          <h1 className="text-kk-cream text-xl font-semibold flex items-center gap-2"><FaTable className="text-purple-300" /> Sheet check{IS_TEST ? " (TEST DB)" : ""}</h1>
          <p className="text-sm text-white/55 max-w-2xl">
            Episodes where the old Google Sheets give a different story time from the database, or list a story the database doesn't have.
            Open an episode, play both times in the editor, and choose. Most urgent first.
          </p>
        </header>

        {err && <p className="text-sm text-red-300">Could not load the sheet check: {err}. (Has db/sheet-review-2026-10-20.sql been run?)</p>}
        {!items && !err && <div className="w-10 h-10 border-4 border-kk-gold/30 border-t-kk-gold rounded-full animate-spin" />}

        {totals && (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-kk-gold/15 p-3"><div className="text-2xl font-mono text-kk-cream">{totals.timesOpen}</div><div className="text-[11px] text-white/45">story times still to check, of {totals.times}</div></div>
            <div className="rounded-xl border border-kk-gold/15 p-3"><div className="text-2xl font-mono text-kk-cream">{totals.missingOpen}</div><div className="text-[11px] text-white/45">sheet-only stories still to check, of {totals.missing}</div></div>
          </div>
        )}

        {items && (
          <div className="flex flex-wrap gap-2 items-center">
            <button className={tab(kind === "times")} onClick={() => setKind("times")}>Story times</button>
            <button className={tab(kind === "missing")} onClick={() => setKind("missing")}>Stories not in the database</button>
            <button className={tab(kind === "all")} onClick={() => setKind("all")}>Both</button>
            <span className="w-px h-5 bg-white/10 mx-1" />
            <button className={tab(show === "open")} onClick={() => setShow("open")}>To do</button>
            <button className={tab(show === "all")} onClick={() => setShow("all")}>Include finished</button>
          </div>
        )}

        <div className="space-y-2">
          {items && rows.length === 0 && <p className="text-sm text-green-300/80 flex items-center gap-2"><FaCheckCircle /> Nothing left to check here.</p>}
          {rows.map((r) => {
            const e = episodes[r.episode_id] || {};
            return (
              <Link key={r.episode_id} to={`/episode-editor/${r.episode_id}#sheet-check`}
                className="block rounded-xl border border-white/10 bg-white/[0.02] hover:border-kk-gold/40 p-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-krushna text-kk-cream leading-snug">{e.title || `Episode ${r.episode_id}`}</div>
                    <div className="text-[11px] text-white/40">{e.publish_date} · Ep {r.episode_id}{e.episode_type ? ` · ${e.episode_type}` : ""}</div>
                  </div>
                  <div className="text-right text-[11px] text-white/50">
                    {r.open > 0 ? <span className="text-kk-cream font-semibold">{r.open} to check</span> : <span className="text-green-300/80">all checked</span>}
                    {r.done > 0 && <div>{r.done} done{r.people.size ? ` · ${[...r.people].join(", ")}` : ""}</div>}
                  </div>
                </div>
                {r.open > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {ORDER.filter((v) => r.counts[v]).map((v) => <span key={v} className={`${pill} ${VERDICTS[v].cls}`}>{r.counts[v]} {VERDICTS[v].label.toLowerCase()}</span>)}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
